/**
 * ApprovalSignOff - HIPAA-compliant doctor sign-off screen
 * 
 * Displays patient info summary, insole details, validation verdict,
 * identity confirmation, and digital signature for approval.
 */

import React, { useState } from 'react'
import type { ValidationReport } from '../../../services/types'
import './ApprovalSignOff.css'

interface ApprovalSignOffProps {
  /** Mesh ID being approved */
  meshId: string
  /** Session ID */
  sessionId: string
  /** Project ID */
  projectId: string
  /** Validation verdict */
  validationReport?: ValidationReport | null
  /** Whether this is an override (REVIEW verdict) */
  isOverride?: boolean
  /** Callback to submit approval */
  onApprove: (signature: string, notes: string) => void
  /** Callback to cancel */
  onCancel: () => void
  /** Loading state */
  isLoading?: boolean
  /** Approval result (set after successful approval) */
  approvalResult?: { audit_log_id: string; approved_at: string } | null
}

export const ApprovalSignOff: React.FC<ApprovalSignOffProps> = ({
  meshId,
  sessionId,
  projectId,
  validationReport,
  isOverride = false,
  onApprove,
  onCancel,
  isLoading = false,
  approvalResult
}) => {
  const [signature, setSignature] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  const [confirmed, setConfirmed] = useState<boolean>(false)

  const canSign = signature.trim().length > 0 && confirmed && (!isOverride || notes.trim().length > 0)

  const handleSubmit = () => {
    if (!canSign) return
    onApprove(signature.trim(), notes.trim())
  }

  if (approvalResult) {
    return (
      <div className="signoff-panel">
        <div className="signoff-success">
          <div className="success-check">✓</div>
          <h3>Insole Approved</h3>
          <div className="signoff-details">
            <div className="detail-row">
              <span className="detail-label">Audit Log ID</span>
              <span className="detail-value">{approvalResult.audit_log_id}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Approved At</span>
              <span className="detail-value">{new Date(approvalResult.approved_at).toLocaleString()}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Mesh ID</span>
              <span className="detail-value">{meshId}</span>
            </div>
          </div>
          <button onClick={onCancel} className="signoff-done-button">
            Done
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="signoff-panel">
      <h3>{isOverride ? 'Override Approval' : 'Doctor Approval'}</h3>

      {isOverride && (
        <div className="signoff-warning">
          Validation did not fully pass. Override requires notes justifying the approval.
        </div>
      )}

      <div className="signoff-summary">
        <h4>Insole Summary</h4>
        <div className="detail-row">
          <span className="detail-label">Project</span>
          <span className="detail-value">{projectId}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Session</span>
          <span className="detail-value">{sessionId.substring(0, 8)}...</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Mesh</span>
          <span className="detail-value">{meshId.substring(0, 8)}...</span>
        </div>
        {validationReport && (
          <div className="detail-row">
            <span className="detail-label">Validation</span>
            <span className={`detail-value ${validationReport.passed ? 'val-pass' : 'val-warn'}`}>
              {validationReport.passed ? 'PASSED' : 'REVIEW'}
              {' '}({validationReport.checks.filter(c => c.passed).length}/{validationReport.checks.length} checks)
            </span>
          </div>
        )}
      </div>

      <div className="signoff-form">
        <div className="form-group">
          <label htmlFor="doctor-signature">
            Digital Signature <span className="required">*</span>
          </label>
          <input
            id="doctor-signature"
            type="text"
            placeholder="Type your full name to sign"
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            disabled={isLoading}
            className="signoff-input"
            autoComplete="off"
          />
        </div>

        <div className="form-group">
          <label htmlFor="approval-notes">
            Notes {isOverride && <span className="required">* (Required for override)</span>}
          </label>
          <textarea
            id="approval-notes"
            placeholder={isOverride ? 'Justification for override approval...' : 'Optional notes...'}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isLoading}
            className="signoff-textarea"
            rows={3}
          />
        </div>

        <div className="form-group">
          <label className="confirmation-label">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              disabled={isLoading}
            />
            <span>
              I confirm that I have reviewed the insole design and validation results, and I authorize this insole for manufacturing.
            </span>
          </label>
        </div>
      </div>

      <div className="signoff-actions">
        <button
          onClick={onCancel}
          disabled={isLoading}
          className="signoff-cancel-button"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={!canSign || isLoading}
          className="signoff-submit-button"
        >
          {isLoading ? 'Signing...' : 'Sign & Approve'}
        </button>
      </div>
    </div>
  )
}
